import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ShieldCheck,
  Compass,
  Lock,
  BadgeDollarSign,
  CreditCard,
  ClipboardCheck,
  X,
} from "lucide-react";

// Hidden on these routes (don't crowd already-CTA-heavy pages)
const HIDDEN_ROUTES = ["/contact", "/privacy", "/terms"];

// Iter-77 — TRUTHFUL TRUST STRIP (formerly "Live · last 5 min" popup).
//
// AUDIT FINDING (iter-77): the previous version of this component
// fabricated a fake "live activity" widget on the client — a sinusoidal
// pseudo-count derived from time-of-day plus `Math.random()` jitter,
// rotating through a hardcoded list of 12 (city + action) tuples like
// "Someone in Charlotte, NC just compared 4 quotes". None of it was
// real. That kind of fake-urgency pattern is exactly what gives generic
// affiliate-comparison sites a sketchy reputation, and it's a clear
// compliance + brand-trust risk for a YMYL fintech product.
//
// FIX: replace with rotating TRUTHFUL trust messages — every line is a
// factual claim about how CarSavr works (verifiable in our own product
// flow: real soft-pull lender integrations, editor-vetted shortlist, no
// auto-dialer, free to use). No counts, no cities, no events.
//
// Future work: if/when we want to surface REAL aggregated activity
// (e.g., from analytics-backed live counts via a backend `/api/stats`
// endpoint) we can re-introduce live numbers — but only after the data
// pipeline exists. Pretending we have data we don't is a no-go.

const TRUST_LINES = [
  {
    eyebrow: "Why CarSavr",
    Icon: Compass,
    title: "Compare in about 2 minutes.",
    body: "One short form, side-by-side quotes from top U.S. providers.",
  },
  {
    eyebrow: "Privacy first",
    Icon: Lock,
    title: "No spam, no robocalls.",
    body: "Your details go to the providers you opt into — nobody else.",
  },
  {
    eyebrow: "Free to use",
    Icon: BadgeDollarSign,
    title: "Always free to compare.",
    body: "We earn a commission only when you accept an offer — never from you.",
  },
  {
    eyebrow: "Credit-safe",
    Icon: CreditCard,
    title: "Soft pull pre-quals.",
    body: "Pre-qualifying with our lender partners doesn't ding your score.",
  },
  {
    eyebrow: "Editor-vetted",
    Icon: ClipboardCheck,
    title: "Hand-picked shortlist.",
    body: "Our editors review every lender + insurer before they appear here.",
  },
];

const DISMISS_KEY = "carsavr:trust-strip-dismissed";

export default function LiveSocialProof() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [idx, setIdx] = useState(0);
  // Iter-116 — Suppress the floating proof widget while any section
  // marked `data-suppress-floating-proof` is in view (e.g., the
  // SavingsCalculator section, whose bottom-left bridge anchor
  // visually overlaps with this widget's anchor point on narrow
  // viewports). IntersectionObserver-driven, GPU-only fade.
  const [suppressed, setSuppressed] = useState(false);

  // Initial dismissal check — session-scoped so the visitor can hide it
  // for the duration of their browse without losing it forever.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const t = sessionStorage.getItem(DISMISS_KEY);
    if (t === "1") setDismissed(true);
  }, []);

  // Reveal after a brief delay; hide on excluded routes
  useEffect(() => {
    if (HIDDEN_ROUTES.includes(pathname) || dismissed) {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(true), 1400);
    return () => clearTimeout(t);
  }, [pathname, dismissed]);

  // Rotate trust lines every 7s. Pause the rotation interval entirely
  // while the widget is hidden so we don't churn re-renders off-screen.
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(() => {
      setIdx((p) => (p + 1) % TRUST_LINES.length);
    }, 7000);
    return () => clearInterval(t);
  }, [visible]);

  // Iter-116 — Wire an IntersectionObserver to any section marked
  // `data-suppress-floating-proof`. The widget fades out while ANY of
  // those sections are visible (≥20%) and fades back in once the user
  // scrolls past them. Uses a Set of intersecting elements so the
  // current state is always derivable without drift.
  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined")
      return;
    const nodes = document.querySelectorAll("[data-suppress-floating-proof]");
    if (!nodes.length) return;
    const intersecting = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersecting.add(entry.target);
          } else {
            intersecting.delete(entry.target);
          }
        });
        setSuppressed(intersecting.size > 0);
      },
      { threshold: 0.2 }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [pathname]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setVisible(false);
    setDismissed(true);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(DISMISS_KEY, "1");
    }
  };

  if (HIDDEN_ROUTES.includes(pathname) || dismissed) return null;

  const line = TRUST_LINES[idx];
  const LineIcon = line.Icon || ShieldCheck;

  return (
    <div
      data-testid="live-social-proof"
      className={`hidden md:flex fixed bottom-24 left-6 z-30 transition-all duration-500 ${
        visible && !suppressed
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-3 pointer-events-none"
      }`}
      aria-live="polite"
    >
      <div className="flex items-stretch bg-white border border-slate-200 shadow-banner max-w-[360px]">
        {/* Iter-89 — icon now matches the active trust eyebrow (no longer a
            fixed shield). Each rotating line carries its own semantic icon
            so the badge actually reads as a different category. */}
        <div
          className="relative flex items-center justify-center px-3 bg-[#0A2540]"
          data-testid="trust-strip-icon-slab"
        >
          <LineIcon
            key={`icon-${idx}`}
            data-testid="trust-strip-icon"
            className="w-4 h-4 text-[#10B981] animate-[fadeIn_0.4s_ease-out]"
          />
        </div>
        <div className="flex-1 px-4 py-3 pr-9">
          <p
            data-testid="trust-strip-eyebrow"
            className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#10B981] mb-0.5"
          >
            {line.eyebrow}
          </p>
          <p
            data-testid="trust-strip-title"
            key={`title-${idx}`}
            className="text-sm font-display font-black text-slate-950 leading-tight animate-[fadeIn_0.4s_ease-out]"
          >
            {line.title}
          </p>
          <p
            data-testid="trust-strip-body"
            key={`body-${idx}`}
            className="text-[11px] text-slate-500 leading-snug mt-1 animate-[fadeIn_0.4s_ease-out]"
          >
            {line.body}
          </p>
          {/* Iter-78 — persistent transparency footer link. Reinforces the
              truthfulness ethic from iter-77 by giving every visitor a
              one-click answer to "how does this site pay the bills?". */}
          <Link
            to="/how-we-make-money"
            data-testid="trust-strip-transparency-link"
            className="mt-2 inline-block text-[10px] font-semibold text-slate-500 hover:text-slate-950 underline-offset-2 hover:underline"
          >
            How we make money →
          </Link>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="live-social-proof-dismiss"
          aria-label="Dismiss trust strip"
          className="absolute top-1.5 right-1.5 w-6 h-6 grid place-items-center text-slate-500 hover:text-slate-950 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
